import type { ClientSummary, ProjectSummary, WebsiteSummary } from "../api/types.js";
import { formatProjectStatus } from "./labels.js";

export type CatalogKind = "client" | "website" | "project";

export interface CatalogHit {
  readonly kind: CatalogKind;
  readonly id: string;
  readonly title: string;
  readonly subtitle: string;
  readonly clientId: string;
}

export interface WorkspaceCatalog {
  readonly clients: readonly ClientSummary[];
  readonly websites: readonly WebsiteSummary[];
  readonly projects: readonly ProjectSummary[];
}

export interface GroupedHits {
  readonly clients: readonly CatalogHit[];
  readonly websites: readonly CatalogHit[];
  readonly projects: readonly CatalogHit[];
}

const includes = (haystack: string, needle: string): boolean =>
  haystack.toLowerCase().includes(needle);

const clientHit = (client: ClientSummary): CatalogHit => ({
  kind: "client",
  id: client.id,
  title: client.name,
  subtitle: client.email ?? client.slug,
  clientId: client.id,
});

const websiteHit = (website: WebsiteSummary): CatalogHit => ({
  kind: "website",
  id: website.id,
  title: website.domain,
  subtitle: website.name,
  clientId: website.clientId,
});

const projectHit = (project: ProjectSummary): CatalogHit => ({
  kind: "project",
  id: project.id,
  title: project.name,
  subtitle: formatProjectStatus(project.status),
  clientId: project.clientId,
});

const take = <T>(items: readonly T[], limit: number): readonly T[] => items.slice(0, limit);

export const groupHits = (hits: readonly CatalogHit[]): GroupedHits => ({
  clients: hits.filter((hit) => hit.kind === "client"),
  websites: hits.filter((hit) => hit.kind === "website"),
  projects: hits.filter((hit) => hit.kind === "project"),
});

/**
 * Local search over an already-loaded catalog. Empty query returns a short
 * suggestion slice so Ctrl+K is useful without a backend search module.
 */
export const searchCatalog = (
  query: string,
  catalog: WorkspaceCatalog,
  suggestionLimit = 6,
): readonly CatalogHit[] => {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) {
    return [
      ...take(catalog.clients, suggestionLimit).map(clientHit),
      ...take(catalog.websites, suggestionLimit).map(websiteHit),
      ...take(catalog.projects, suggestionLimit).map(projectHit),
    ];
  }

  const hits: CatalogHit[] = [];
  for (const client of catalog.clients) {
    const email = client.email ?? "";
    if (
      includes(client.name, needle) ||
      includes(client.slug, needle) ||
      (email.length > 0 && includes(email, needle))
    ) {
      hits.push(clientHit(client));
    }
  }
  for (const website of catalog.websites) {
    if (includes(website.domain, needle) || includes(website.name, needle)) {
      hits.push(websiteHit(website));
    }
  }
  for (const project of catalog.projects) {
    if (includes(project.name, needle)) {
      hits.push(projectHit(project));
    }
  }
  return hits;
};
